import { useMemo } from "react";
import { useDocuments } from "./useDocuments";
import { useQuizzes } from "./useQuizzes";
import {
  type DocumentSummary,
  type IdAndTitle,
  type QuizStatus,
  type QuizSummary,
} from "../types";

export interface AnchoringUser {
  firstName: string;
  lastName?: string;
  email?: string | null;
  imageUrl?: string | null;
}

interface VersionColumns {
  documentId: number;
  documentVersionId: number;
  title: string;
  category: IdAndTitle;
  owner: AnchoringUser;
  publisher: AnchoringUser | null;
  publishedAt: string | null;
  adoptedAt: string | null;
  tags: string[];
}

export interface AnchoredDocumentRow extends VersionColumns {
  quizId: number;
  status: QuizStatus;
  createdBy: QuizSummary["createdBy"];
  createdAt: string;
  invitedCount: number;
  completedCount: number;
}

export type UnanchoredDocumentRow = VersionColumns;

const toVersionColumns = (
  document: DocumentSummary,
  version: NonNullable<DocumentSummary["latestPublishedVersion"]>,
): VersionColumns => ({
  documentId: document.id,
  documentVersionId: version.id,
  title: version.title,
  category: document.category,
  owner: document.owner,
  publisher: document.publisher,
  publishedAt: version.publishedAt,
  adoptedAt: version.adoptedAt,
  tags: document.tags.map((tag) => tag.title),
});

/**
 * Published documents split into those that have a quiz (anchored) and those
 * that do not (unanchored). Each row represents a document's latest published
 * version (matching the library). Quiz-side data comes from `useQuizzes`.
 */
export function useAnchoringDocuments() {
  const documents = useDocuments({ includeArchived: false });
  const quizzes = useQuizzes();

  const { anchored, unanchored } = useMemo(() => {
    const quizByVersionId = new Map(
      (quizzes.data ?? []).map((quiz) => [quiz.documentVersionId, quiz]),
    );

    const anchoredRows: AnchoredDocumentRow[] = [];
    const unanchoredRows: UnanchoredDocumentRow[] = [];

    for (const document of documents.data ?? []) {
      const version = document.latestPublishedVersion;
      if (!version) continue;

      const columns = toVersionColumns(document, version);
      const quiz = quizByVersionId.get(version.id);

      if (quiz) {
        anchoredRows.push({
          ...columns,
          quizId: quiz.id,
          status: quiz.status,
          createdBy: quiz.createdBy,
          createdAt: quiz.createdAt,
          invitedCount: quiz.invitedCount,
          completedCount: quiz.completedCount,
        });
      } else {
        unanchoredRows.push(columns);
      }
    }

    unanchoredRows.sort((a, b) => a.title.localeCompare(b.title));

    return { anchored: anchoredRows, unanchored: unanchoredRows };
  }, [documents.data, quizzes.data]);

  return {
    anchored,
    unanchored,
    isLoading: documents.isLoading || quizzes.isLoading,
    error: documents.error ?? quizzes.error,
    mutate: quizzes.mutate,
  };
}
